import { useEffect, useState } from "react";
import { TranslationService } from "../../services/TranslationService";
import Criteria from "./component/Criteria";
import { CriteriaProvider } from "./providers/CriteriaContext";
import { FormProvider } from "./providers/FormContext";
import ErrorMessage from "../shared/components/Error";
import SegmentService from "../../services/SegmentService";
import Dropdown from "../shared/components/Dropdown";
import Label from "../shared/form/Label";
import CompanyService from "../../services/CompanyService";
import { ToastService } from "../shared/bootstrap/Toast";
import FloatingPanelService from "../shared/FloatingPanel";
import Loading from "../shared/components/Loading";
import { Observable } from "rxjs";
import { handleErrorWithToast } from "../../utils/RequestUtils";
import ModalService from "../shared/bootstrap/modal/Modal";
import { MapIf } from "../../utils/Utils";
import { AxiosError } from "axios";
import { SegmentGetResponse } from "../../entities/segment/SegmentGetResponse";
import { SegmentListResponse } from "../../entities/segment/SegmentListResponse";
import { RequiredManager } from "../shared/RequieredManager";
import { Operator } from "./providers/CriteriaContext";
import { LoadingWithMessages } from "../shared/components/LoadingWithMessages";

export type SegmentModel = Partial<{
  [K in keyof SegmentGetResponse.Item]: K extends "SegmentFolderID"
    ? number | null
    : SegmentGetResponse.Item[K];
}>;
let segmentTemplate: SegmentModel = {
  Name: "",
  AlwaysVisible: true,
  SegmentFolderID: null,
  Priority: null,
  Criteria: []
};

const EditSegment = ({
  id = undefined,
  folders,
  reload,
  onDeleteEvent = undefined
}: {
  id?: number;
  folders:
    | SegmentListResponse
    | Observable<SegmentListResponse | undefined>
    | undefined;
  reload: () => void;
  onDeleteEvent?: () => void;
}) => {
  const { translate } = TranslationService;
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folderList, setfolderList] = useState(
    folders instanceof Observable ? undefined : folders
  );
  const [cloneCount, setCloneCount] = useState(1);

  const requiredManager = new RequiredManager();

  useEffect(() => {
    if (folders === undefined) {
      const getFolders = async () => {
        const result = await handleErrorWithToast(
          SegmentService.getList(),
          () => FloatingPanelService.hidePanel()
        );
        if (!result) {
          return;
        }
        setfolderList(result);
      };
      getFolders();
      return;
    }
    if (!(folders instanceof Observable)) {
      return;
    }
    const sub = folders.subscribe((x) => setfolderList(x));
    return () => sub.unsubscribe();
  }, [folders]);

  const getDefaultData = (
    id: number | undefined,
    folders: SegmentListResponse | undefined
  ): SegmentModel => {
    if (!id || folders === undefined) {
      return Object.assign({}, segmentTemplate);
    }
    const segment = folders.list.reduce<
      SegmentListResponse.Segment | undefined
    >((prev, x) => prev ?? x.items.find((y) => y.SegmentID === id), undefined);
    if (!segment) {
      return Object.assign({}, segmentTemplate);
    }
    // Preservar IsSuperSegment y SuperSegmentID del backend al obtener datos desde el listado
    return {
      ...segment,
      IsSuperSegment: segment.IsSuperSegment ?? false,
      SuperSegmentID: segment.SuperSegmentID ?? null
    } as SegmentModel;
  };

  const [data, setData] = useState<SegmentModel>(
    getDefaultData(id, folderList)
  );
  const shouldDisable = loading;

  useEffect(() => {
    const getData = async () => {
      if (id) {
        setLoading(true);
        setError(false);
        const result = await SegmentService.get(id);
        if (result instanceof Error) {
          setError(true);
          setLoading(false);
          return;
        }
        let criteria: any[] = [];
        result.item.Criteria.forEach((criterion, index) => {
          if (criterion.ConditionOR) {
            criterion.or = 2;
          } else {
            criterion.or = 1;
          }
          criterion.key = "def" + Math.random();
          // Completar Order si viene null
          if (criterion.Order === null || criterion.Order === undefined) {
            criterion.Order = index;
          }
          criteria.push(criterion);
        });
        // Ordenar criterios por Order si está definido
        criteria.sort((a, b) => {
          const orderA = a.Order ?? 0;
          const orderB = b.Order ?? 0;
          return orderA - orderB;
        });
        result.item.Criteria = criteria;
        // Asegurar que IsSuperSegment y SuperSegmentID se preserven del backend
        // Estas propiedades son la fuente de verdad para identificar super segmentos y segmentos hijos
        setData({
          ...result.item,
          IsSuperSegment: result.item.IsSuperSegment ?? false,
          SuperSegmentID: result.item.SuperSegmentID ?? null
        });
        setLoading(false);
      }
    };
    setData(getDefaultData(id, folderList));
    getData();
  }, [setError, setData, setLoading, id, folderList]);

  const edit =
    <T extends keyof SegmentModel>(key: T) =>
    (value: SegmentModel[T]) =>
      setData((x) => ({ ...x, [key]: value }));

  const SaveButton = () => {
    const handleEditSegment = async () => {
      let segment = data;

      if (!requiredManager.validate()) {
        return;
      }

      if (segment.Criteria === undefined || segment.Criteria.length === 0) {
        ToastService.showToast(TranslationService.translate.SelectCriteria);
        return;
      }
      if (!segment.Name || segment.Name.length === 0) {
        ToastService.showToast(TranslationService.translate.EnterSegmentName);
        return;
      }
      if (!segment.SegmentFolderID) {
        segment.SegmentFolderID = null;
      }

      // Identificar si es segmento hijo: si tiene SuperSegmentID, es hijo y no se puede modificar
      const isChildSegmentToSave =
        segment.SuperSegmentID != null && segment.SuperSegmentID !== undefined;

      if (isChildSegmentToSave) {
        // Es un segmento hijo: mantener IsSuperSegment y SuperSegmentID del backend
        // No se deben modificar estas propiedades en segmentos hijos
        // El backend es la fuente de verdad para estos valores
      } else {
        // No es segmento hijo: detectar si es super segmento basándose en los criterios
        // Un segmento es super segmento si tiene al menos un criterio con operador Dynamic en un campo de tipo lista
        const definitions = SegmentService.getCriteria(translate);
        const isSuperSegment = segment.Criteria.some((criterion: any) => {
          // Verificar si el operador es Dynamic
          if (criterion.Operator === Operator.Dynamic) {
            // Buscar la definición del criterio para verificar si es de tipo lista
            const criterionDef = definitions.criterionList.find(
              (def: any) => def.id === criterion.Field
            );
            if (criterionDef) {
              // Si es Client o Invoice, buscar la definición adicional
              if (criterionDef.type === -1 || criterionDef.type === -2) {
                const additionalDefs =
                  criterionDef.type === -1
                    ? definitions.clientDefs
                    : definitions.invoiceDefs;
                const additionalDef = additionalDefs.find(
                  (def: any) => def.AdditionalDefinitionID === criterion.Data
                );
                // Verificar si es tipo lista (type === 1)
                return additionalDef?.type === 1;
              }
            }
          }
          return false;
        });

        segment.IsSuperSegment = isSuperSegment;
        segment.SuperSegmentID = null;
      }

      setError(false);
      setSaving(true);
      const result = await SegmentService.set(segment);
      setSaving(false);
      if (result instanceof Error) {
        const axisError = result as AxiosError<{ Message: string }>;
        if (
          axisError.response?.status === 400 &&
          axisError.response.data.Message === "Segment Name"
        ) {
          ModalService.showDefaultModal({
            title: TranslationService.translate.Error,
            message: TranslationService.translate.SegmentNameAlreadyExists
          });
        } else {
          setError(true);
        }
      } else {
        reload();
        FloatingPanelService.hidePanel();
        ToastService.showToast(
          TranslationService.translate.SegmentProcessingMessage,
          "",
          "success"
        );

        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    };
    return (
      <button className="btn btn-primary ms-auto" onClick={handleEditSegment}>
        {translate.Save}
      </button>
    );
  };

  // Identificar segmento hijo usando SuperSegmentID (propiedad del backend)
  // Si tiene SuperSegmentID, es un segmento hijo y no se puede editar ni eliminar
  const isChildSegment =
    data.SuperSegmentID != null && data.SuperSegmentID !== undefined;

  const onDelete = () => {
    if (!data.SegmentID || isChildSegment) {
      return;
    }
    const deleteSegment = async () => {
      setError(false);
      setLoading(true);
      // Si es un super segmento, usar timeout de 15 minutos (900000 ms), sino usar el default (60000 ms)
      const timeout = data.IsSuperSegment === true ? 900000 : undefined;
      const result = await SegmentService.delete(data.SegmentID!, timeout);
      if (result instanceof Error) {
        setError(true);
        setLoading(false);
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      } else {
        onDeleteEvent && onDeleteEvent();
        reload();
        ToastService.showToast(
          TranslationService.translate.SegmentDeletedSuccessfully,
          undefined,
          "success"
        );
        FloatingPanelService.hidePanel();
      }
    };
    ModalService.showDefaultModal({
      onAcceptClick: deleteSegment,
      acceptButtonLabel: TranslationService.translate.Delete,
      acceptButtonClassName: "btn btn-danger",
      message: TranslationService.translate.SureDeleteSegment,
      title: TranslationService.translate.Delete
    });
  };

  const cloneModal = () => {
    const count = MapIf(cloneCount, (x) => !Number.isInteger(x), 1);
    const cloneSegment = async () => {
      setError(false);
      setLoading(true);
      const result = await SegmentService.clone(data.SegmentID!, count);
      if (result instanceof Error) {
        setError(true);
        setLoading(false);
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      } else {
        reload();
        ToastService.showToast(
          TranslationService.translate.SegmentCloned,
          undefined,
          "success"
        );
        FloatingPanelService.hidePanel();
      }
    };
    ModalService.showDefaultModal({
      onAcceptClick: cloneSegment,
      acceptButtonLabel: TranslationService.translate.Clone,
      acceptButtonClassName: "btn btn-primary",
      message:
        count > 1
          ? TranslationService.translate.SureCloneManySegment.replace(
              "{cant}",
              count.toString()
            )
          : TranslationService.translate.SureCloneSegment,
      title: TranslationService.translate.Clone
    });
  };

  if (loading || folderList === undefined) {
    return <Loading />;
  }

  if (saving) {
    return (
      <LoadingWithMessages
        messages={[
          TranslationService.translate.WaitingSaveSegment1,
          TranslationService.translate.WaitingSaveSegment2,
          TranslationService.translate.WaitingSaveSegment3
        ]}
      />
    );
  }

  return (
    <>
      <div className="floatingBody p-4">
        <FormProvider data={data} disabled={shouldDisable}>
          {!error ? (
            <>
              {isChildSegment && (
                <div className="alert alert-info mb-3">
                  {TranslationService.translate.SegmentIsChildOfSuperSegment ||
                    "Este segmento es hijo de un super segmento y no se puede editar ni eliminar."}
                </div>
              )}
              <div className="input-column">
                <label className="form-label">
                  {TranslationService.translate.Name}&nbsp;
                </label>
              </div>

              <div className="d-flex align-items-center p-0 m-0 gap-3">
                <div className="col-8">
                  <input
                    className="form-control align-items-center"
                    onChange={(e) => edit("Name")(e.target.value)}
                    defaultValue={data.Name}
                    disabled={isChildSegment || shouldDisable}
                  />
                </div>

                <div className="col-4 align-items-center form-check gap-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    defaultChecked={data.AlwaysVisible}
                    onChange={(e) => edit("AlwaysVisible")(e.target.checked)}
                    disabled={isChildSegment || shouldDisable}
                  />
                  <label className=" form-check-label p-0 m-0">
                    {TranslationService.translate.AlwaysVisible}
                  </label>
                </div>
              </div>

              <div className="d-flex align-items-center p-0 m-0 gap-3">
                <Label
                  className="col-8 mt-2"
                  label={TranslationService.translate.Folder}
                >
                  <Dropdown
                    onChange={edit("SegmentFolderID")}
                    selectClasses="w-100"
                    optionLabel={translate.None}
                    items={folderList.list.map((x) => ({
                      text: x.name ?? "",
                      value: x.id,
                      key: x.id
                    }))}
                    defaultValue={data.SegmentFolderID}
                    disabled={isChildSegment || shouldDisable}
                  />
                </Label>
                {CompanyService.getSetting("enablesegmentpriority") && (
                  <Label
                    className="col-4 mt-2"
                    label={TranslationService.translate.Priority}
                  >
                    <Dropdown
                      onChange={edit("Priority")}
                      selectClasses="w-100"
                      optionLabel={translate.None}
                      items={Array.from({
                        length: CompanyService.getTotalPriorities() - 1
                      }).map((x, i) => ({
                        text: (i + 1).toString(),
                        value: i + 1
                      }))}
                      defaultValue={data.Priority}
                      disabled={isChildSegment || shouldDisable}
                    />
                  </Label>
                )}
              </div>

              <h5 className="h5 mb-0 mt-3">{translate.Criteria}</h5>
              {!isChildSegment ? (
                <CriteriaProvider data={data} setData={setData}>
                  <Criteria requiredManager={requiredManager} />
                </CriteriaProvider>
              ) : (
                <div className="alert alert-warning">
                  {TranslationService.translate
                    .SegmentChildCriteriaCannotBeEdited ||
                    "Los criterios de segmentos hijos no se pueden editar."}
                </div>
              )}
            </>
          ) : (
            <ErrorMessage
              onRefresh={() => {
                setError(false);
              }}
              message="An error has ocurred"
            />
          )}
        </FormProvider>
      </div>

      <nav className="floatingFooter">
        <div className="container-fluid px-4">
          <div className="d-flex justify-content-end w-100">
            <div className="d-flex flex-row flex-wrap gap-3">
              {data.SegmentID && !isChildSegment && (
                <>
                  {CompanyService.canDo("editsegment") && (
                    <button
                      className="btn btn-danger ms-auto"
                      onClick={onDelete}
                    >
                      {translate.Delete}
                    </button>
                  )}
                  <button
                    className="btn btn-secondary ms-auto show-child-on-hover show-child-on-hover d-flex"
                    onClick={cloneModal}
                  >
                    {translate.Clone}
                    <input
                      style={{ width: 30, height: 20 }}
                      defaultValue={cloneCount}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onChange={(e) => {
                        setCloneCount(parseInt(e.target.value));
                      }}
                      className="mx-1 px-1 display-when-hovering-parent"
                    />
                  </button>
                </>
              )}
              {!isChildSegment && <SaveButton />}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default EditSegment;
